'use client';

/**
 * `useSubmitTransaction` — the SDK-owned transaction-submission hook (web).
 *
 * Relocated from frontend/src/lib/tx-engine/** as part of the auth boundary
 * migration (spec 009 FILE 14 Group E). The 3,663-LOC transaction-submission
 * engine now lives platform-neutral in `src/auth/tx-engine/`; this hook is
 * the web entry point.
 *
 * Why a hook, why platform/web: the engine core is vendor-neutral, but the
 * host-specific bindings it needs — Statsig feature gates, the per-network
 * RPC endpoint resolver, the Better Stack logger, the typed-analytics event
 * emitter — are forbidden in SDK core (.claude/rules/platform-agnostic.md).
 * The host (frontend) supplies them as injected callbacks (`UseSubmitTransactionDeps`),
 * exactly the pattern the auth-provider relocation used (`SdkAuthProviderCallbacks`).
 * Statsig and `@/lib/network` NEVER enter the SDK — the app threads them here.
 *
 * The hook also owns engine init/teardown: it lazily initializes the engine
 * on first use with the resolved connection config and tears it down on
 * unmount.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useAuthSync } from '../../../../auth/oauth/auth-sync.js';
import { DEFAULT_BLOCKHASH_RESOLVER_MODE } from '../../../../blockchain/blockhash/index.js';
import { TxErrorCode } from '../../../../tx-engine/errors/errors.js';
import { initTxEngine, teardownTxEngine } from '../../../../tx-engine/index.js';
import { resolveSubmissionConnectionConfig } from '../../../../tx-engine/submit/connection-scope.js';
/**
 * Returns an imperative `submitTransaction` function. The engine is lazily
 * initialized on first call and torn down on unmount.
 *
 * The returned function is referentially stable — the latest `deps` are read
 * through a ref, so callers can pass an unstable `deps` object without
 * churning the callback identity.
 */
export function useSubmitTransaction(deps) {
  const depsRef = useRef(deps);
  depsRef.current = deps;
  // Dead-session auto-recovery: a `TX_SESSION_EXPIRED` result means the
  // Turnkey signing session is expired or mis-scoped — no retry can succeed.
  // Fan out the existing session-expired logout so the app transitions to a
  // clean signed-out state instead of leaving the user with a signed-in UI
  // whose every transaction fails.
  const {
    signalSessionExpired
  } = useAuthSync();
  // Build the engine config from the latest deps on every submission.
  const buildConfig = useCallback(() => {
    const d = depsRef.current;
    return {
      logger: d.logger,
      emitEvent: d.emitEvent,
      resolveConnectionConfig: d.resolveConnectionConfig,
      resolveFlags: d.resolveFlags,
      onForeground: d.onForeground,
      impersonationActive: d.impersonationActive,
      isDev: d.isDev ?? false
    };
  }, []);
  const initializedRef = useRef(false);
  // Lazily init the engine on first use; tear down on unmount.
  const ensureInit = useCallback(cfg => {
    if (initializedRef.current) return;
    initTxEngine({
      connectionConfig: cfg.resolveConnectionConfig(),
      logger: cfg.logger,
      onForeground: cfg.onForeground,
      resolveBlockhashResolverMode: () => cfg.resolveFlags().blockhashResolverMode ?? DEFAULT_BLOCKHASH_RESOLVER_MODE
    });
    initializedRef.current = true;
  }, []);
  useEffect(() => {
    return () => {
      if (initializedRef.current) {
        teardownTxEngine(depsRef.current.logger);
        initializedRef.current = false;
      }
    };
  }, []);
  return useCallback(async (buildResult, options) => {
    const inputConfig = buildConfig();
    const connectionConfig = resolveSubmissionConnectionConfig(options, inputConfig);
    const cfg_0 = {
      ...inputConfig,
      resolveConnectionConfig: () => connectionConfig
    };
    ensureInit(cfg_0);
    // Lazy import keeps the heavy engine graph (@solana/web3.js,
    // @coral-xyz/anchor) out of the initial bundle until first submit.
    const {
      submitTransaction
    } = await import('../../../../tx-engine/index.js');
    const result = await submitTransaction(buildResult, options, cfg_0);
    if (!result.ok && result.error.code === TxErrorCode.TX_SESSION_EXPIRED) {
      signalSessionExpired('transaction-signing');
    }
    return result;
  }, [buildConfig, ensureInit, signalSessionExpired]);
}